import { useAuthStore } from '@/stores/auth';
import { hasAnyPermission, hasAllPermissions, type UserPermission } from '@/types/auth';

/**
 * Hook to check if the current user has a specific permission
 *
 * @param permission - The permission to check (e.g., 'incidents:create')
 * @returns boolean indicating if user has the permission
 *
 * @example
 * ```tsx
 * const canCreateIncidents = usePermission('incidents:create');
 *
 * return (
 *   <button disabled={!canCreateIncidents}>
 *     Create Incident
 *   </button>
 * );
 * ```
 */
export function usePermission(permission: UserPermission): boolean {
  return useAuthStore((state) => state.checkPermission(permission));
}

/**
 * Hook to check if the current user has ANY of the specified permissions
 *
 * @param permissions - Array of permissions to check
 * @returns boolean indicating if user has at least one permission
 *
 * @example
 * ```tsx
 * const canAccessIncidents = useAnyPermission([
 *   'incidents:read',
 *   'incidents:create',
 *   'incidents:update'
 * ]);
 * ```
 */
export function useAnyPermission(permissions: UserPermission[]): boolean {
  return useAuthStore((state) => {
    const user = state.user;
    if (!user) {return false;}

    // Wildcard-aware so `['*']` holders resolve correctly (L-026 consistency).
    return hasAnyPermission(user.permissions, permissions);
  });
}

/**
 * Hook to check if the current user has ALL of the specified permissions
 *
 * @param permissions - Array of permissions to check
 * @returns boolean indicating if user has all permissions
 *
 * @example
 * ```tsx
 * const canManageIncidents = useAllPermissions([
 *   'incidents:create',
 *   'incidents:update',
 *   'incidents:delete'
 * ]);
 * ```
 */
export function useAllPermissions(permissions: UserPermission[]): boolean {
  return useAuthStore((state) => {
    const user = state.user;
    if (!user) {return false;}

    // Wildcard-aware so `['*']` holders resolve correctly (L-026 consistency).
    return hasAllPermissions(user.permissions, permissions);
  });
}

/**
 * Hook to get all current user permissions
 *
 * @returns Array of user permissions or empty array if not authenticated
 *
 * @example
 * ```tsx
 * const permissions = usePermissions();
 * console.log('User permissions:', permissions);
 * ```
 */
export function usePermissions(): string[] {
  return useAuthStore((state) => state.user?.permissions ?? []);
}
